import { recommendationsForTier } from '../data/modelRegistry'
import type {
  Assessment,
  ExecutionMode,
  ModelTier,
  ReasoningEffort,
  SignalReason,
  WorkItem,
} from '../domain/types'
import { analyzeWorkItem } from './analyze'
import {
  inferWorkItemContext,
  type InferredWorkItemContext,
} from './inferWorkItemContext'

export type OutcomeLensId = 'fastest' | 'best-fit' | 'safest'

export type OutcomeLensContext = Pick<
  WorkItem,
  | 'executionMode'
  | 'availableTools'
  | 'stakes'
  | 'targetQuality'
>

export type OutcomeLensAssessment = {
  lens: OutcomeLensId
  context: OutcomeLensContext
  workItem: WorkItem
  assessment: Assessment
}

export type FastestTierFloor = {
  minimumTier: ModelTier
  noDowngrade: boolean
  signalIds: string[]
}

export type OutcomeLensResult = {
  inferredContext: InferredWorkItemContext
  toolAssumptions: string[]
  fastestFloor: FastestTierFloor
  fastest: OutcomeLensAssessment
  bestFit: OutcomeLensAssessment
  safest: OutcomeLensAssessment
  ordering: {
    valid: true
    fastestRank: number
    bestFitRank: number
    safestRank: number
  }
}

const TIER_RANK: Record<ModelTier, number> = {
  direct: 0,
  standard: 1,
  deep: 2,
}
const TIERS: ModelTier[] = ['direct', 'standard', 'deep']

// These reuse the analyzer's existing reason threshold for tool dependence
// and apply the same threshold to verification burden.
const HIGH_VERIFICATION_OR_TOOL_THRESHOLD = 45
const HIGH_EXECUTION_RISK_THRESHOLD = 60

function adjacentTier(
  tier: ModelTier,
  direction: -1 | 1,
): ModelTier {
  const rank = TIER_RANK[tier]
  return TIERS[Math.min(2, Math.max(0, rank + direction))] ?? tier
}

function effortForTier(
  tier: ModelTier,
  assessment: Assessment,
): ReasoningEffort {
  if (tier === 'deep') {
    return assessment.signals.estimatedSteps >= 9
      ? 'xhigh'
      : 'high'
  }
  if (tier === 'standard') {
    return 'medium'
  }
  return assessment.taskDemand < 18 &&
    assessment.executionRisk < 25
    ? 'none'
    : 'low'
}

export function withAssessmentTier(
  assessment: Assessment,
  tier: ModelTier,
  reason: SignalReason,
): Assessment {
  return {
    ...assessment,
    tier,
    effort: effortForTier(tier, assessment),
    reasons: [
      ...assessment.reasons.filter(
        ({ id }) => !id.startsWith('outcome-lens-'),
      ),
      reason,
    ],
    providerRecommendations: recommendationsForTier(tier),
  }
}

function toolAssumptionsForMode(
  executionMode: ExecutionMode,
): string[] {
  if (executionMode === 'research') {
    return ['web search']
  }
  if (
    executionMode === 'code-edit' ||
    executionMode === 'agent'
  ) {
    return ['repository', 'tests']
  }
  return []
}

function fastestFloorFor(
  inferredContext: InferredWorkItemContext,
  bestFit: Assessment,
): FastestTierFloor {
  const signalIds: string[] = []
  const explicitHighStakes =
    inferredContext.stakes.source ===
      'explicit-prompt-signal' &&
    inferredContext.stakes.value === 'high'
  const explicitProduction =
    inferredContext.targetQuality.source ===
      'explicit-prompt-signal' &&
    inferredContext.targetQuality.value === 'production'
  const highExecutionRisk =
    bestFit.executionRisk >= HIGH_EXECUTION_RISK_THRESHOLD
  const highVerification =
    bestFit.signals.verificationBurden >=
    HIGH_VERIFICATION_OR_TOOL_THRESHOLD
  const highToolDependence =
    bestFit.signals.toolDependence >=
    HIGH_VERIFICATION_OR_TOOL_THRESHOLD

  if (explicitHighStakes) {
    signalIds.push('explicit-high-stakes')
  }
  if (highExecutionRisk) {
    signalIds.push('execution-risk-60')
  }
  if (explicitProduction) {
    signalIds.push('explicit-production')
  }
  if (highVerification) {
    signalIds.push('verification-burden-45')
  }
  if (highToolDependence) {
    signalIds.push('tool-dependence-45')
  }

  const noDowngrade = explicitHighStakes || highExecutionRisk
  return {
    minimumTier: noDowngrade
      ? bestFit.tier
      : explicitProduction ||
          highVerification ||
          highToolDependence
        ? 'standard'
        : 'direct',
    noDowngrade,
    signalIds,
  }
}

function lensAssessment(
  lens: OutcomeLensId,
  workItem: WorkItem,
  assessment: Assessment,
): OutcomeLensAssessment {
  const stableWorkItem = {
    ...workItem,
    availableTools: [...workItem.availableTools],
  }

  return {
    lens,
    context: {
      executionMode: stableWorkItem.executionMode,
      availableTools: [...stableWorkItem.availableTools],
      stakes: stableWorkItem.stakes,
      targetQuality: stableWorkItem.targetQuality,
    },
    workItem: stableWorkItem,
    assessment,
  }
}

function fastestReason(
  bestFitTier: ModelTier,
  fastestTier: ModelTier,
  floor: FastestTierFloor,
): SignalReason {
  if (fastestTier !== bestFitTier) {
    return {
      id: 'outcome-lens-fastest',
      label: 'Fastest adjacent tier',
      detail:
        'Uses one tier below Best-fit while staying above the deterministic safety floor.',
      impact: 'down',
    }
  }
  if (floor.signalIds.length > 0) {
    return {
      id: 'outcome-lens-fastest-floor',
      label: 'Fastest safety floor',
      detail: `No lower tier is allowed because of: ${floor.signalIds.join(', ')}.`,
      impact: 'risk',
    }
  }
  return {
    id: 'outcome-lens-fastest-minimum',
    label: 'Already at the fastest tier',
    detail: 'Best-fit already uses direct, the lowest available tier.',
    impact: 'down',
  }
}

export function applyOutcomeLensPolicy(
  workItem: WorkItem,
  inferredContext: InferredWorkItemContext,
  bestFitAssessment: Assessment,
  options: {
    minimumFastestTier?: ModelTier
  } = {},
): OutcomeLensResult {
  const bestFitRank = TIER_RANK[bestFitAssessment.tier]
  const fastestFloor = fastestFloorFor(
    inferredContext,
    bestFitAssessment,
  )
  const proposedFastest = adjacentTier(
    bestFitAssessment.tier,
    -1,
  )
  const policyFastestTier =
    TIERS[
      Math.max(
        TIER_RANK[proposedFastest],
        TIER_RANK[fastestFloor.minimumTier],
      )
    ] ?? bestFitAssessment.tier
  const fastestTier =
    TIERS[
      Math.max(
        TIER_RANK[policyFastestTier],
        options.minimumFastestTier
          ? TIER_RANK[options.minimumFastestTier]
          : 0,
      )
    ] ?? policyFastestTier
  const effectiveFastestFloor =
    fastestTier === policyFastestTier
      ? fastestFloor
      : {
          ...fastestFloor,
          minimumTier: fastestTier,
          signalIds: [
            ...fastestFloor.signalIds,
            'conservative-preview-floor',
          ],
        }
  const safestTier = adjacentTier(bestFitAssessment.tier, 1)
  const fastestAssessment = withAssessmentTier(
    bestFitAssessment,
    fastestTier,
    fastestReason(
      bestFitAssessment.tier,
      fastestTier,
      effectiveFastestFloor,
    ),
  )
  const bestFit = withAssessmentTier(
    bestFitAssessment,
    bestFitAssessment.tier,
    {
      id: 'outcome-lens-best-fit',
      label: 'Best-fit policy baseline',
      detail:
        'Uses the automatic inferred-context route without a tier adjustment.',
      impact: 'readiness',
    },
  )
  const safest = withAssessmentTier(
    bestFitAssessment,
    safestTier,
    {
      id: 'outcome-lens-safest',
      label: 'Safest adjacent tier',
      detail:
        safestTier === bestFitAssessment.tier
          ? 'Best-fit already uses deep, the highest available tier.'
          : 'Uses one tier above Best-fit without changing the inferred task context.',
      impact: 'up',
    },
  )
  const fastestRank = TIER_RANK[fastestAssessment.tier]
  const safestRank = TIER_RANK[safest.tier]

  if (
    fastestRank > bestFitRank ||
    bestFitRank > safestRank
  ) {
    throw new Error(
      `Outcome lens ordering invariant failed: ${fastestAssessment.tier} <= ${bestFit.tier} <= ${safest.tier}.`,
    )
  }

  return {
    inferredContext,
    toolAssumptions: [...workItem.availableTools],
    fastestFloor: effectiveFastestFloor,
    fastest: lensAssessment(
      'fastest',
      workItem,
      fastestAssessment,
    ),
    bestFit: lensAssessment('best-fit', workItem, bestFit),
    safest: lensAssessment('safest', workItem, safest),
    ordering: {
      valid: true,
      fastestRank,
      bestFitRank,
      safestRank,
    },
  }
}

export function assessOutcomeLenses(
  prompt: string,
  availableTools?: readonly string[],
): OutcomeLensResult {
  const inferredContext = inferWorkItemContext(prompt)
  const toolAssumptions =
    availableTools === undefined
      ? toolAssumptionsForMode(
          inferredContext.executionMode.value,
        )
      : [...availableTools]
  const workItem: WorkItem = {
    prompt,
    executionMode: inferredContext.executionMode.value,
    availableTools: toolAssumptions,
    stakes: inferredContext.stakes.value,
    targetQuality: inferredContext.targetQuality.value,
  }

  return applyOutcomeLensPolicy(
    workItem,
    inferredContext,
    analyzeWorkItem(workItem),
  )
}
